/**
 * TGQuickFilters
 * Filter row rendered under table header
 * Enhanced with keyboard shortcuts
 */

import React, { useState, useCallback, useRef } from 'react';
import { Input, InputNumber, Select, DatePicker, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { spacing } from '../../../theme/designTokens';
import { debounce } from 'lodash';

const { RangePicker } = DatePicker;

/**
 * @param {Object} props
 * @param {Array} props.columns - Columns with filter config
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - (key, value) => void
 */
function TGQuickFilters({ columns, filters, onFilterChange }) {
  // Local state for text inputs (debounced)
  const [localTextValues, setLocalTextValues] = useState({});
  const debouncedRef = useRef({});

  // Debounced filter change
  const debouncedFilterChange = useCallback(
    debounce((key, value) => {
      onFilterChange(key, value);
    }, 300),
    [onFilterChange]
  );

  const handleTextChange = (key, value) => {
    setLocalTextValues((prev) => ({ ...prev, [key]: value }));
    debouncedFilterChange(key, value);
  };

  // Immediate filter change (bypass debounce)
  const applyFilterImmediately = (key, value) => {
    setLocalTextValues((prev) => ({ ...prev, [key]: value }));
    debouncedFilterChange.cancel(); // Cancel pending debounced calls
    onFilterChange(key, value);
  };

  // Clear filter
  const clearFilter = (key) => {
    setLocalTextValues((prev) => ({ ...prev, [key]: '' }));
    onFilterChange(key, null);
  };

  // Keyboard handler for inputs
  const handleKeyDown = (e, key, currentValue) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyFilterImmediately(key, currentValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      clearFilter(key);
      e.target.blur();
    }
  };

  const renderFilter = (col) => {
    if (!col.filterable || !col.filterType) return null;

    const filterValue = filters[col.key];
    const localValue = localTextValues[col.key];

    const commonProps = {
      allowClear: true,
      placeholder: `Filter ${col.title.toLowerCase()}`,
      style: { width: '100%' },
    };

    switch (col.filterType) {
      case 'text':
        return (
          <Input
            {...commonProps}
            value={localValue !== undefined ? localValue : filterValue || ''}
            onChange={(e) => handleTextChange(col.key, e.target.value)}
            onKeyDown={(e) =>
              handleKeyDown(
                e,
                col.key,
                localValue !== undefined ? localValue : filterValue || ''
              )
            }
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
            aria-label={`Filter ${col.title} by text`}
            title="Press Enter to apply immediately, Esc to clear"
          />
        );

      case 'number':
        return (
          <InputNumber
            {...commonProps}
            value={filterValue}
            onChange={(value) => onFilterChange(col.key, value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onFilterChange(col.key, filterValue);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onFilterChange(col.key, null);
                e.target.blur();
              }
            }}
            aria-label={`Filter ${col.title} by number`}
            title="Press Enter to apply, Esc to clear"
          />
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            value={filterValue}
            onChange={(value) => onFilterChange(col.key, value)}
            options={col.filterOptions}
            aria-label={`Filter ${col.title}`}
          />
        );

      case 'multiselect':
        return (
          <Select
            {...commonProps}
            mode="multiple"
            value={filterValue || []}
            onChange={(value) => onFilterChange(col.key, value)}
            options={col.filterOptions}
            maxTagCount={1}
            aria-label={`Filter ${col.title} (multiple selection)`}
          />
        );

      case 'date':
        return (
          <DatePicker
            {...commonProps}
            value={filterValue}
            onChange={(value) => onFilterChange(col.key, value)}
            aria-label={`Filter ${col.title} by date`}
          />
        );

      case 'dateRange':
        return (
          <RangePicker
            {...commonProps}
            value={filterValue}
            onChange={(value) => onFilterChange(col.key, value)}
            aria-label={`Filter ${col.title} by date range`}
          />
        );

      case 'range':
        return (
          <div style={{ display: 'flex', gap: spacing.xs }}>
            <InputNumber
              placeholder="Min"
              value={filterValue?.min}
              onChange={(value) =>
                onFilterChange(col.key, { ...filterValue, min: value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onFilterChange(col.key, { ...filterValue, min: undefined });
                  e.target.blur();
                }
              }}
              style={{ flex: 1 }}
              aria-label={`Filter ${col.title} minimum value`}
            />
            <InputNumber
              placeholder="Max"
              value={filterValue?.max}
              onChange={(value) =>
                onFilterChange(col.key, { ...filterValue, max: value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onFilterChange(col.key, { ...filterValue, max: undefined });
                  e.target.blur();
                }
              }}
              style={{ flex: 1 }}
              aria-label={`Filter ${col.title} maximum value`}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <tr className="tg-filter-row" role="row">
      {columns.map((col) => (
        <th
          key={col.key}
          style={{
            padding: `${spacing.xs}px ${spacing.sm}px`,
            backgroundColor: '#FAFAFA',
            borderBottom: '1px solid #E4E1E8',
          }}
          role="columnheader"
        >
          {renderFilter(col)}
        </th>
      ))}
    </tr>
  );
}

export default TGQuickFilters;
